import {skipToken} from '@reduxjs/toolkit/query';
import {Button, DataStatus, EmptyStatus, openDialog} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {useEffect, useRef} from 'react';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetBusinessCaseQuery, useListBusinessCaseTestDrivesQuery} from '@dms/api/sales';
import {
  openCreateTestDriveDialog,
  TEST_DRIVE_DIALOG_ID,
  TestDriveDialog,
} from '@dms/features/test-drive';
import i18n from '@dms/i18n';
import {Main, RestrictedArea, usePermissions} from '@dms/shared';

import {isNotNilOrEmpty, parseDate, useQueryState, useRequiredParams} from 'shared/web';

import {businessCaseSearchParams} from '../../../businessCaseSearchParams';
import {TestDriveCard} from './TestDriveCard';

export function BusinessCaseTestDriveV2() {
  const {id: businessCaseId} = useRequiredParams();

  const {
    data: businessCase,
    isLoading: isBusinessCaseLoading,
    isError: isBusinessCaseError,
  } = useGetBusinessCaseQuery({businessCaseId});

  const {data: testDrives, isLoading: areTestDrivesLoading} = useListBusinessCaseTestDrivesQuery({
    businessCaseId,
  });

  const customerId = businessCase?.customerId;

  const {isLoading: isCustomerLoading} = useGetCustomerV2Query(
    isNotNil(customerId) ? {customerId} : skipToken
  );

  const [hasReadTestDrivePermission, hasCreateTestDrivePermission] = usePermissions({
    permissionKeys: ['readTestDrive', 'createTestDrive'],
  });

  const [editTestDriveId, , clearEditTestDriveId] = useQueryState(
    businessCaseSearchParams.testDriveEdit
  );

  const processedEditIdRef = useRef<string | null>(null);

  useEffect(
    function openEditTestDriveDialog() {
      if (!editTestDriveId || !testDrives || areTestDrivesLoading) {
        return;
      }

      if (
        isNotNilOrEmpty(processedEditIdRef.current) &&
        processedEditIdRef.current === editTestDriveId
      ) {
        return;
      }

      const targetTestDrive = testDrives.find((td) => td.id === editTestDriveId);

      if (!targetTestDrive) {
        return;
      }

      processedEditIdRef.current = editTestDriveId;

      openDialog(
        <TestDriveDialog
          businessCaseId={businessCaseId}
          vehicleId={targetTestDrive.vehicleId}
          testDrive={targetTestDrive}
        />,
        {
          id: TEST_DRIVE_DIALOG_ID,
          title: i18n.t('entity.testDrive.labels.editTestDrive'),
          size: 'large',
          withAdditionalFooter: true,
          onCloseComplete() {
            processedEditIdRef.current = null;
            clearEditTestDriveId();
          },
        }
      );

      return () => {
        processedEditIdRef.current = null;
      };
    },
    [editTestDriveId, testDrives, areTestDrivesLoading, businessCaseId, clearEditTestDriveId]
  );

  const handleNewTestDriveDialogOpen = () =>
    openCreateTestDriveDialog({
      vehicleId: businessCase?.offers?.[0]?.saleVehicles?.[0]?.vehicleId,
      businessCaseId,
      customerId,
    });

  const isLoading = isBusinessCaseLoading || areTestDrivesLoading;
  const isError = isBusinessCaseError;

  const sortedTestDrives = testDrives
    ? [...testDrives].sort(
        (a, b) => parseDate(b.scheduledAt).getTime() - parseDate(a.scheduledAt).getTime() // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
      )
    : [];

  return (
    <Main>
      <DataStatus isLoading={isLoading} isError={isError}>
        <VStack spacing={4}>
          <Show when={isNotNilOrEmpty(testDrives)}>
            <HStack width="100%" align="center" justify="flex-end">
              <Button
                title={i18n.t('entity.testDrive.labels.createTestDrive')}
                isDisabled={!hasCreateTestDrivePermission}
                isLoading={isCustomerLoading}
                onClick={handleNewTestDriveDialogOpen}
              />
            </HStack>

            <Show when={hasReadTestDrivePermission}>
              <DataStatus isLoading={isLoading} isError={isError}>
                {sortedTestDrives.map((testDrive) => (
                  <TestDriveCard
                    testDrive={testDrive}
                    key={testDrive.id}
                    businessCaseId={businessCaseId}
                  />
                ))}
              </DataStatus>
            </Show>

            <Show when={!hasReadTestDrivePermission}>
              <RestrictedArea shouldHideRedirectButton />
            </Show>
          </Show>

          <Show when={isNilOrEmpty(testDrives)}>
            <Box padding={11}>
              <EmptyStatus
                variant="vehicle"
                headline={i18n.t('entity.testDrive.labels.noTestDriveForOpportunity')}
                action={{
                  title: i18n.t('entity.testDrive.labels.scheduleTestDrive'),
                  variant: 'primary',
                  leftIcon: 'content/add_circle',
                  onClick: handleNewTestDriveDialogOpen,
                  isDisabled: !hasCreateTestDrivePermission,
                  isLoading: isCustomerLoading,
                }}
              />
            </Box>
          </Show>
        </VStack>
      </DataStatus>
    </Main>
  );
}
